import { useEffect, useMemo, useRef } from "react";
import { type QueryKey, useQueries } from "@tanstack/react-query";
import type { ChunkPlaceholder } from "./combineChunkQueries";
import { combineChunkQueries } from "./combineChunkQueries";

const NO_CHUNKS: ReadonlyMap<string, never> = new Map<string, never>();

/** The data a chunk last showed for its own query, fetched or derived. */
export interface ShownChunk<TQuery, TData> {
    query: TQuery;
    data: TData;
}

/**
 * One query per chunk of a range, once `scopeId` (a workspace or organization)
 * is known. A loading chunk shows the data last shown for the same
 * `toChunkKey`, which useQueries drops for a new key, until the scope or
 * bucket changes: derived for its query by `toPlaceholder` when it can be,
 * else kept as it was. A render with no chunks keeps it.
 */
export function useChunkQueries<TQuery extends { bucket?: string }, TData>({
    scopeId,
    queries,
    toChunkKey,
    toQueryOptions,
    toPlaceholder,
}: {
    scopeId: string;
    queries: readonly TQuery[];
    /** The chunk a query counts, the same across filter changes, e.g. its start. */
    toChunkKey: (query: TQuery) => string;
    toQueryOptions: (query: TQuery) => {
        queryKey: QueryKey;
        queryFn: () => Promise<TData>;
        staleTime?: number;
        gcTime?: number;
    };
    /** `query`'s placeholder, derived from what its chunk showed for another query; undefined when it can't be. */
    toPlaceholder?: (params: {
        shown: ShownChunk<TQuery, TData>;
        query: TQuery;
    }) => ChunkPlaceholder<TData> | undefined;
}) {
    const shownKey = `${scopeId}/${queries[0]?.bucket}`;
    const shownRef = useRef({
        key: "",
        chunks: new Map<string, ShownChunk<TQuery, TData>>(),
    });
    const shown: ReadonlyMap<string, ShownChunk<TQuery, TData>> = shownRef
        .current.key === shownKey
        ? shownRef.current.chunks
        : NO_CHUNKS;
    const placeholders = useMemo(
        () =>
            queries.map((query): ChunkPlaceholder<TData> | undefined => {
                const chunk = shown.get(toChunkKey(query));
                if (!chunk || chunk.query === query) {
                    return chunk && { data: chunk.data, isExact: true };
                }
                return (
                    toPlaceholder?.({ shown: chunk, query }) ?? {
                        data: chunk.data,
                        isExact: false,
                    }
                );
            }),
        [shown, queries, toChunkKey, toPlaceholder],
    );
    const result = useQueries({
        queries: queries.map((query, index) => ({
            ...toQueryOptions(query),
            enabled: scopeId !== "",
            placeholderData: placeholders[index]?.data,
        })),
        combine: (results) => combineChunkQueries({ results, placeholders }),
    });
    useEffect(() => {
        if (queries.length === 0) {
            return;
        }
        const chunks = new Map(
            queries.flatMap(
                (query, index): Array<[string, ShownChunk<TQuery, TData>]> => {
                    const chunkKey = toChunkKey(query);
                    const kept = shown.get(chunkKey);
                    const data = result.ownResponses[index];
                    const isKept =
                        data === undefined ||
                        (kept?.query === query && kept.data === data);
                    const chunk = isKept ? kept : { query, data };
                    return chunk ? [[chunkKey, chunk]] : [];
                },
            ),
        );
        const isUnchanged =
            shownRef.current.chunks === shown &&
            chunks.size === shown.size &&
            [...chunks].every(
                ([chunkKey, chunk]) => shown.get(chunkKey) === chunk,
            );
        if (!isUnchanged) {
            shownRef.current = { key: shownKey, chunks };
        }
    });
    return result;
}
